import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { NotFound } from '@client/components/NotFound';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type ExcludedCategory, ExcludedCategoryForm } from './ExcludedCategoryForm';

export const Route = createFileRoute('/console/$orgId/mspr/msp-exceptions/edit/$id')({
    component: EditExcludedCategory,
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    staticData: {
        presentation: 'overlay',
        permissions: ['msp:read'],
    },
});

function EditExcludedCategory() {
    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const orgId = useOrgId();
    const user = useUser({ throwOnUndefined: false });
    const canReadMspr = hasPermission(user?.roles ?? [], 'msp:read');
    const { mutate: upsertExcludedCategory, isPending: isSubmitting } =
        trpc.mspr.category.upsertExcludedCategory.useMutation();
    // Query the requested category directly so deep links do not depend on the current list page.
    const { data: categoriesResult, isPending: isPendingCategories } =
        trpc.mspr.category.listExcludedCategories.useQuery(
            { where: { id }, limit: 1 },
            { enabled: canReadMspr, refetchOnWindowFocus: false },
        );
    const categoryData = categoriesResult?.rows[0];

    if (!canReadMspr) {
        return <NotFound />;
    }

    // Create a data object with category info for the form
    const data = categoryData
        ? {
              id: id, // Use category ID as placeholder since we're dealing with categories
              riskSignedOffDate: categoryData.riskSignedOffDate,
              rationaleForExclusion: categoryData.rationaleForExclusion,
              ArrangementCategory: { name: categoryData.name },
          }
        : undefined;

    const isLoading = isPendingCategories || !categoryData;

    const handleSubmit = (values: ExcludedCategory) => {
        message.loading('Saving excluded category...');
        upsertExcludedCategory(
            {
                arrangementCategoryId: id,
                riskSignedOffDate: values.riskSignedOffDate ?? null,
                rationaleForExclusion: values.rationaleForExclusion ?? null,
            },
            {
                onSuccess: () => {
                    message.success('Excluded category saved');
                    trpcUtils.mspr.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    errorMessage.show(error);
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <ExcludedCategoryForm
                open={open}
                data={data}
                state={match({ isLoading, isSubmitting })
                    .with({ isLoading: true }, () => 'loading' as const)
                    .with({ isSubmitting: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({ to: '/console/$orgId/mspr/msp-exceptions', params: { orgId } });
                }}
            />
        </PageController>
    );
}
